import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { useParams } from "react-router-dom";

import Icons from "@/components/icons";
import { Button } from "@/components/ui/v2/button";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { useIntegrationModule } from "@/features/integrations/hooks/useIntegrationModule";
import { ModuleName } from "@/features/integrations/utils/module-name";
import {
  useClientWebhookRegistrationsInfinite,
  useClientWebhookConfiguration,
  useDeregisterRecruitNowWebhook,
  useRegisterRecruitNowWebhook,
  useReregisterRecruitNowWebhook,
  useWebhookRegistrationById,
} from "@/features/recruitnow/webhooks/api";
import { ClientWebhookComparison } from "@/features/recruitnow/webhooks/components/ClientWebhookComparison";
import { WebhookRegistrationList } from "@/features/recruitnow/webhooks/components/WebhookRegistrationList";
import { useAuth } from "@/lib/hooks/useAuth";

export function ClientWebhookContent() {
  const { t } = useTranslation();
  const { clientId } = useParams<{ clientId: string }>();
  const { tenantId } = useAuth();

  const { data: webhookConfig, isLoading: webhookConfigLoading } = useClientWebhookConfiguration(clientId);
  const clientWebhookId = webhookConfig?.webhookId ?? null;

  // Check if job-source module is active for this client
  const { data: jobSourceModule, isLoading: moduleLoading } = useIntegrationModule({
    integrationName: "Cockpit",
    moduleName: ModuleName.JobSource,
    tenantId: tenantId ?? undefined,
    clientId: clientId ?? undefined,
  });

  const isModuleActive = jobSourceModule?.isActive ?? false;

  // Fetch the matching registration by ID separately
  const {
    data: matchingRegistration,
    isLoading: matchingRegistrationLoading,
  } = useWebhookRegistrationById({
    clientId,
    registrationId: clientWebhookId,
  });

  // Fetch other registrations excluding the matching one
  const {
    data: registrationsData,
    isLoading: registrationsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useClientWebhookRegistrationsInfinite({
    clientId,
    excludeId: clientWebhookId,
  });

  const deregisterMutation = useDeregisterRecruitNowWebhook();
  const registerMutation = useRegisterRecruitNowWebhook();
  const reregisterMutation = useReregisterRecruitNowWebhook();

  const isLoading = webhookConfigLoading || registrationsLoading || matchingRegistrationLoading || moduleLoading;

  const otherRegistrations = useMemo(() => {
    if (!registrationsData?.pages) return [];
    return registrationsData.pages.flatMap((page) => page.items);
  }, [registrationsData]);

  const handleDelete = (registrationId: string) => {
    if (clientId && registrationId) {
      deregisterMutation.mutate({ clientId, registrationId });
    }
  };

  const handleRegister = () => {
    if (clientId) {
      registerMutation.mutate({ clientId });
    }
  };

  return (
    <div className="space-y-8">
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : !clientWebhookId ? (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground">
            {t("webhooks.recruitnow.currentRegistration")}
          </h2>
          <div className="rounded-md border border-dashed border-border p-10 text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              {isModuleActive
                ? t("webhooks.recruitnow.noRegistrationConfigured")
                : t("common.inactive")}
            </p>
            <Button
              onClick={handleRegister}
              disabled={registerMutation.isPending || !isModuleActive}
              variant="default"
            >
              <Icons.SyncIcon fontSize={16} className={registerMutation.isPending ? "animate-spin" : ""} />
              {registerMutation.isPending
                ? t("common.loading")
                : t("webhooks.recruitnow.register")}
            </Button>
          </div>
        </section>
      ) : (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground">
            {t("webhooks.recruitnow.currentRegistration")}
          </h2>
          <ClientWebhookComparison
            webhookId={webhookConfig?.webhookId ?? null}
            webhookSecret={webhookConfig?.webhookSecret ?? null}
            matchingRegistration={matchingRegistration ?? null}
            onReregister={() => {
              if (clientId) {
                reregisterMutation.mutate({ clientId });
              }
            }}
            onDelete={handleDelete}
            isReregistering={reregisterMutation.isPending}
            isDeleting={deregisterMutation.isPending}
          />
        </section>
      )}

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">
          {t("webhooks.recruitnow.otherRegistrationsTitle")}
        </h2>
        <InfiniteScroll
          dataLength={otherRegistrations.length}
          next={fetchNextPage || (() => {})}
          hasMore={hasNextPage || false}
          loader={
            isFetchingNextPage && otherRegistrations.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))}
              </div>
            ) : null
          }
          scrollThreshold={0.9}
          scrollableTarget="main-content-container"
        >
          <WebhookRegistrationList
            registrations={otherRegistrations}
            onDelete={handleDelete}
            isDeleting={deregisterMutation.isPending}
            isLoading={registrationsLoading}
          />
        </InfiniteScroll>
      </section>
    </div>
  );
}
